import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import SleepVideoV3Service from '../../services/SleepVideoV3Service';
import VoiceOverConfigService from '../../services/VoiceOverConfigService';
import VideoThumbnailPanel from '../../components/VideoThumbnailPanel';
import AuthService from '../../services/AuthService';
import ChannelFootageNotice from '../../components/ChannelFootageNotice';
import { SCRIPT_STATUS_CHIP, ACTIVE_RENDER_STATUSES, POLL_INTERVAL, VOICE_OVER_DEFAULT_BY_PROFILE } from './constants';
import PipelineStepper from './PipelineStepper';
import ScriptSection from './ScriptSection';
import VoiceOverSection from './VoiceOverSection';
import RenderSection from './RenderSection';

function getAudioDuration(file) {
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration);
      URL.revokeObjectURL(audio.src);
    });
    audio.addEventListener('error', () => {
      resolve(null);
      URL.revokeObjectURL(audio.src);
    });
    audio.src = URL.createObjectURL(file);
  });
}

export default function SleepVideoV3DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [videoTitle, setVideoTitle] = useState('');
  const [videoContext, setVideoContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [showFullScript, setShowFullScript] = useState(false);
  const [copiedFullScript, setCopiedFullScript] = useState(false);
  const messagesEndRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const voFileInputRef = useRef(null);
  const [rendering, setRendering] = useState(false);
  const [togglingUsed, setTogglingUsed] = useState(false);
  const [generatingVoiceOver, setGeneratingVoiceOver] = useState(false);
  const [generateVoiceOverError, setGenerateVoiceOverError] = useState(null);
  const [voiceOverConfigs, setVoiceOverConfigs] = useState([]);
  const [selectedVoiceOverConfigForGenerate, setSelectedVoiceOverConfigForGenerate] = useState(null);
  const pollRef = useRef(null);

  const loadDoc = useCallback(async () => {
    if (!id) return;
    try {
      const data = await SleepVideoV3Service.get(id);
      setDoc(data);
      setVideoTitle(data.videoTitle ?? data.title ?? '');
      setVideoContext(data.videoContext ?? '');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    loadDoc().finally(() => setLoading(false));
  }, [loadDoc]);

  const initialDocLoaded = useRef(false);
  useEffect(() => {
    if (doc && !initialDocLoaded.current) {
      initialDocLoaded.current = true;
      setVideoTitle(doc.videoTitle ?? doc.title ?? '');
      setVideoContext(doc.videoContext ?? '');
    }
    if (!doc) initialDocLoaded.current = false;
  }, [doc]);

  useEffect(() => {
    if (doc?.id) {
      VoiceOverConfigService.list()
        .then((list) => setVoiceOverConfigs(Array.isArray(list) ? list : []))
        .catch(() => setVoiceOverConfigs([]));
    }
  }, [doc?.id]);

  // Reset voice-over selection when navigating to a different video
  const prevDocIdRef = useRef(null);
  useEffect(() => {
    if (doc?.id !== prevDocIdRef.current) {
      prevDocIdRef.current = doc?.id ?? null;
      setSelectedVoiceOverConfigForGenerate(null);
    }
  }, [doc?.id]);

  // Default voice-over config by profile number when video has no saved config (only when nothing selected yet)
  useEffect(() => {
    if (!doc?.id || !voiceOverConfigs.length || doc.voiceOverConfigId || selectedVoiceOverConfigForGenerate != null) return;
    const pn = doc.channel?.profileNumber;
    if (pn == null) return;
    const num = Number(pn);
    if (Number.isNaN(num)) return;
    let voiceId = null;
    if (num >= VOICE_OVER_DEFAULT_BY_PROFILE.low.min && num <= VOICE_OVER_DEFAULT_BY_PROFILE.low.max) {
      voiceId = VOICE_OVER_DEFAULT_BY_PROFILE.low.voice_id;
    } else if (num >= VOICE_OVER_DEFAULT_BY_PROFILE.high.min && num <= VOICE_OVER_DEFAULT_BY_PROFILE.high.max) {
      voiceId = VOICE_OVER_DEFAULT_BY_PROFILE.high.voice_id;
    }
    if (!voiceId) return;
    const config = voiceOverConfigs.find((c) => c.voice_id === voiceId);
    if (config) setSelectedVoiceOverConfigForGenerate(config);
  }, [doc?.id, doc?.voiceOverConfigId, doc?.channel?.profileNumber, voiceOverConfigs, selectedVoiceOverConfigForGenerate]);

  // When doc has a saved voice config, show it in the dropdown (user can still change it)
  useEffect(() => {
    if (!doc?.voiceOverConfigId || !voiceOverConfigs.length || selectedVoiceOverConfigForGenerate != null) return;
    const config = voiceOverConfigs.find((c) => c.id == doc.voiceOverConfigId);
    if (config) setSelectedVoiceOverConfigForGenerate(config);
  }, [doc?.id, doc?.voiceOverConfigId, voiceOverConfigs, selectedVoiceOverConfigForGenerate]);

  useEffect(() => {
    const hasActiveScript =
      doc?.generationStatus?.status === 'generating' ||
      doc?.generationStatus?.status === 'queued' ||
      doc?.generationStatus?.status === 'rate limit';
    const hasActiveRender = ACTIVE_RENDER_STATUSES.includes(doc?.renderStatus?.status);
    const hasActiveVoiceOver =
      doc?.voiceOverGenerationStatus?.status === 'queued' ||
      doc?.voiceOverGenerationStatus?.status === 'generating' ||
      doc?.voiceOverGenerationStatus?.status === 'generating-voiceover';
    if (!hasActiveScript && !hasActiveRender && !hasActiveVoiceOver) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => loadDoc(), POLL_INTERVAL);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [doc?.generationStatus?.status, doc?.renderStatus?.status, doc?.voiceOverGenerationStatus?.status, loadDoc]);

  const handleGenerateRef = useRef(null);
  handleGenerateRef.current = async () => {
    const titleTrimmed = videoTitle.trim();
    const contextTrimmed = videoContext.trim();
    if (!titleTrimmed || !contextTrimmed) return;
    try {
      setGenerating(true);
      setGenerateError(null);
      await SleepVideoV3Service.generate(id, {
        videoTitle: titleTrimmed,
        videoContext: contextTrimmed,
        generationStep: 'script',
      });
      await loadDoc();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to start generation';
      setGenerateError(msg);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (
      loading ||
      !doc ||
      doc.generationStatus?.status !== 'idle' ||
      !location.state?.startGenerate
    )
      return;
    const titleTrimmed = (doc.videoTitle || doc.title || '').trim();
    const contextTrimmed = (doc.videoContext || '').trim();
    if (!titleTrimmed || !contextTrimmed) return;
    navigate(`/sleep-videos/${id}`, { replace: true, state: {} });
    handleGenerateRef.current?.();
  }, [loading, doc, doc?.generationStatus?.status, location.state?.startGenerate, id, navigate]);

  const handleGenerate = useCallback(async () => {
    await handleGenerateRef.current?.();
  }, []);

  const handleVoiceoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError(null);
    setSuccess(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const duration = await getAudioDuration(file);
      const { presignedUrl, objectName } = await SleepVideoV3Service.requestVoiceoverPresignedUrl(id, {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'audio/mpeg',
      });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });
      await SleepVideoV3Service.completeVoiceoverUpload(id, {
        objectName,
        fileSize: file.size,
        duration,
      });
      setSuccess('Voice over uploaded successfully');
      await loadDoc();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleGenerateVoiceOver = async () => {
    const configId = doc?.voiceOverConfigId || selectedVoiceOverConfigForGenerate?.id;
    if (!configId) return;
    if (!doc?.fullScript?.trim()) return;
    try {
      setGeneratingVoiceOver(true);
      setGenerateVoiceOverError(null);
      await SleepVideoV3Service.generate(id, {
        generationStep: 'voice-over',
        voiceOverConfigId: configId,
      });
      setSuccess('Voice over generation started');
      await loadDoc();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to generate voice over';
      setGenerateVoiceOverError(msg);
    } finally {
      setGeneratingVoiceOver(false);
    }
  };

  const handleRender = async () => {
    setError(null);
    setSuccess(null);
    setRendering(true);
    try {
      await SleepVideoV3Service.generate(id, { generationStep: 'video-render' });
      setSuccess('Render queued');
      await loadDoc();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to start render';
      setError(msg);
    } finally {
      setRendering(false);
    }
  };

  const handleBackgroundMusicChange = async (e) => {
    const value = e.target.checked;
    setError(null);
    setSuccess(null);
    try {
      await SleepVideoV3Service.update(id, { useBackgroundMusic: value });
      setDoc((prev) => (prev ? { ...prev, useBackgroundMusic: value } : null));
      setSuccess('Background music preference saved');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update preference');
    }
  };

  const handleToggleUsed = async () => {
    if (!id || doc == null) return;
    setTogglingUsed(true);
    try {
      await SleepVideoV3Service.update(id, { used: !(doc.used ?? false) });
      setDoc((prev) => (prev ? { ...prev, used: !(prev.used ?? false) } : null));
      setSuccess((doc.used ?? false) ? 'Marked as unused' : 'Marked as used');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setTogglingUsed(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error && !doc) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sleep-videos')}>
          Back to Sleep Videos
        </Button>
      </Box>
    );
  }

  const status = doc?.generationStatus ?? { status: 'idle' };
  const messages = doc?.messages ?? [];
  const genStatus = status.status || 'idle';
  const scriptChip = SCRIPT_STATUS_CHIP[genStatus] || SCRIPT_STATUS_CHIP.idle;
  const progress = status.progress || 0;
  const stepLabel = status.stepLabel || '';
  const isGenerating = genStatus === 'generating' || genStatus === 'queued' || genStatus === 'rate limit';
  const isCompleted = genStatus === 'completed';
  const isFailed = genStatus === 'failed';
  const canGenerate = genStatus === 'idle' || isFailed;
  const renderActive = ACTIVE_RENDER_STATUSES.includes(doc?.renderStatus?.status);
  const footageRenderBlockedReason =
    doc?.voiceOverObjectName &&
    (!doc?.footageFolderIds || doc.footageFolderIds.length === 0)
      ? 'Configure a footage folder on the Channels page before rendering.'
      : null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/sleep-videos')}
            sx={{ color: 'white', textTransform: 'none' }}
          >
            Back to Sleep Videos
          </Button>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              color: 'white',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              mt: 1,
            }}
          >
            {doc?.title || 'Sleep Video V3'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'flex-end' }}>
          <Chip
            label={doc?.used ? 'Used' : 'Unused'}
            color={doc?.used ? undefined : 'default'}
            size='small'
            sx={
              doc?.used
                ? {
                    fontWeight: 600,
                    bgcolor: '#e91e63',
                    color: 'white',
                    '&:hover': { bgcolor: '#d81b60' },
                    py: 1,
                    px: 1.25,
                  }
                : {
                    bgcolor: 'white',
                    color: 'text.primary',
                    fontWeight: 600,
                    border: '1px solid rgba(0,0,0,0.12)',
                    py: 1,
                    px: 1.25,
                  }
            }
          />
          <Button
            variant='contained'
            color={doc?.used ? 'success' : 'secondary'}
            onClick={handleToggleUsed}
            disabled={togglingUsed}
            sx={{ textTransform: 'none' }}
          >
            {togglingUsed ? 'Updating...' : doc?.used ? 'Mark as unused' : 'Mark as used'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PipelineStepper
        doc={doc}
        videoTitle={videoTitle}
        videoContext={videoContext}
        generating={generating}
        generatingVoiceOver={generatingVoiceOver}
        rendering={rendering}
        handleGenerate={handleGenerate}
        handleGenerateVoiceOver={handleGenerateVoiceOver}
        handleRender={handleRender}
        hasVoiceOverConfig={!!(doc?.voiceOverConfigId || selectedVoiceOverConfigForGenerate?.id)}
        scriptDebugScreenshotUrl={doc?.debugScreenshotUrl}
      />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <ScriptSection
            doc={doc}
            videoTitle={videoTitle}
            videoContext={videoContext}
            generating={generating}
            generateError={generateError}
            onDismissGenerateError={() => setGenerateError(null)}
            scriptChip={scriptChip}
            generationStatus={doc?.generationStatus}
            progress={progress}
            stepLabel={stepLabel}
            isGenerating={isGenerating}
            isCompleted={isCompleted}
            isFailed={isFailed}
            canGenerate={canGenerate}
            messages={messages}
            showFullScript={showFullScript}
            setShowFullScript={setShowFullScript}
            copiedFullScript={copiedFullScript}
            setCopiedFullScript={setCopiedFullScript}
            handleGenerate={handleGenerate}
            messagesEndRef={messagesEndRef}
            videoId={doc?.id}
            onScriptSaved={loadDoc}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <VoiceOverSection
            doc={doc}
            voiceOverConfigs={voiceOverConfigs}
            selectedVoiceOverConfigForGenerate={selectedVoiceOverConfigForGenerate}
            setSelectedVoiceOverConfigForGenerate={setSelectedVoiceOverConfigForGenerate}
            uploading={uploading}
            uploadProgress={uploadProgress}
            generatingVoiceOver={generatingVoiceOver}
            generateVoiceOverError={generateVoiceOverError}
            onDismissGenerateVoiceOverError={() => setGenerateVoiceOverError(null)}
            handleVoiceoverSelect={handleVoiceoverSelect}
            handleGenerateVoiceOver={handleGenerateVoiceOver}
            handleBackgroundMusicChange={handleBackgroundMusicChange}
            voFileInputRef={voFileInputRef}
          />
          <ChannelFootageNotice channel={doc.channel} kind="sleep" folder={doc.footageFolders?.[0] || null} />
          <VideoThumbnailPanel key={id} kind='sleep' videoId={id} title={doc?.title} />
          <RenderSection
            doc={doc}
            rendering={rendering}
            handleRender={handleRender}
            renderActive={renderActive}
            renderBlockedReason={footageRenderBlockedReason}
          />
        </Box>
      </Box>
    </Box>
  );
}
